// react
import { MouseEvent, useContext, useState } from 'react';

// types
import { Tag, Feedback } from '../../../@types';

// i18n
import { Trans, useTranslation } from 'react-i18next';

// router
import { useSearchParams } from 'react-router-dom';

// context
import { ContextBanner } from '../../../App.context';

// hooks
import { useFetch } from '../../../hooks/useFetch';

// mui
import { Alert, AlertTitle, Grid, Typography } from '@mui/material';

// dialogs
import { DialogActions } from '../DialogActions/DialogActions';
import { Loading } from '../../ui/Loading/Loading';

// props
export interface DialogDeleteTagProps {
	isOpen: boolean;
	onClose: (e?: MouseEvent<HTMLButtonElement | HTMLAnchorElement>) => void;
	onSuccess?: () => void;
}

export const DialogDeleteTag = ({ isOpen, onClose, onSuccess }: DialogDeleteTagProps) => {
	// session
	const accessToken = sessionStorage.getItem('accessToken');
	const idToken = sessionStorage.getItem('idToken');

	// hooks
	const [searchParams] = useSearchParams();
	const { t } = useTranslation();

	// params
	const paramDialog = searchParams.get('dialog');
	const paramTag = searchParams.get('dialogTag');

	// context
	const { setBanner } = useContext(ContextBanner);

	// state
	const [tag, setTag] = useState<Tag | null>(null);
	const [feedback, setFeedback] = useState<Feedback | null>(null);

	// fetch tag
	const { isLoading: isLoadingTag } = useFetch({
		isEnabled: Boolean(paramTag && paramDialog === 'deleteTag'),
		url: `${process.env.REACT_APP_API_URL}/tags/${paramTag}`,
		options: {
			headers: {
				Authorization: `Bearer ${accessToken}`,
				User: String(idToken),
			},
		},
		onSuccess: (res) => {
			if (res.data) {
				setTag(res.data[0]);
			}
		},
	});

	// delete tag
	const { fetchRequest: deleteTag, isLoading: isLoadingDeleteTag } = useFetch({
		isLazy: true,
		url: `${process.env.REACT_APP_API_URL}/tags/${paramTag}`,
		options: {
			method: 'DELETE',
			headers: {
				Authorization: `Bearer ${accessToken}`,
				User: String(idToken),
			},
		},
		onError: (error) => {
			const feedback: Feedback = {
				alert: {
					children: t('error.default'),
				},
			};

			if (error.id === 'TAG_TRANSACTIONS_EXIST') {
				feedback.alert.children = t('error.tagTransactionsExist');
				feedback.title = {
					children: t('tagCannotBeDeleted'),
				};
			}

			if (error.id === 'TAG_UNAUTHORIZED_DELETE') {
				feedback.alert.children = t('error.unauthorized');
			}

			setFeedback(feedback);
		},
		onSuccess: () => {
			// set banner
			setBanner({
				hasClose: true,
				message: t('alert.tagDeleted', { name: tag?.name }),
				severity: 'success',
			});

			if (onSuccess) {
				onSuccess();
			}

			// close dialog
			if (onClose) {
				onClose();
			}
		},
	});

	// handlers
	const handleSubmit = (e: MouseEvent<HTMLButtonElement | HTMLAnchorElement>) => {
		// clear feedback
		setFeedback(null);

		// delete tag
		deleteTag();
	};

	const handleClose = (e: MouseEvent<HTMLButtonElement | HTMLAnchorElement>) => {
		// clear feedback
		setFeedback(null);

		if (onClose) {
			onClose(e);
		}
	};

	const renderContent = () => {
		if (isLoadingTag) {
			return (
				<Grid item xs={12}>
					<Grid alignItems="center" container justifyContent="center">
						<Grid item>
							<Loading />
						</Grid>
					</Grid>
				</Grid>
			);
		}

		return (
			<>
				<Grid item xs={12}>
					<Trans
						components={{
							strong: <Typography color="brand.blue.600" component="span" fontWeight={600} />,
						}}
						i18nKey="dialogDeleteTag.content"
						values={{ name: tag?.name }}
					/>
				</Grid>
				<Grid item xs={12}>
					<Alert severity="warning">{t('alert.deleteTag')}</Alert>
				</Grid>
				{feedback && (
					<Grid item xs={12}>
						<Alert severity="error" {...feedback.alert}>
							{feedback.title && <AlertTitle {...feedback.title} />}
							{feedback.alert.children}
						</Alert>
					</Grid>
				)}
			</>
		);
	};

	return (
		<DialogActions
			actions={[
				{
					children: t('cancel'),
					onClick: handleClose,
					variant: 'text',
				},
				{
					children: t('deleteTag'),
					color: 'error',
					loading: isLoadingDeleteTag,
					onClick: handleSubmit,
					variant: 'contained',
				},
			]}
			alignItems="center"
			IconCircleProps={{
				type: 'tag',
			}}
			isOpen={isOpen}
			maxWidth={400}
			onClose={handleClose}
			subtitle={{
				children: isLoadingTag ? '' : tag?.name,
			}}
			title={{
				children: t('deleteTag'),
			}}>
			<Grid container spacing={2}>
				{renderContent()}
			</Grid>
		</DialogActions>
	);
};
